import React from 'react';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { process } from '@progress/kendo-data-query';

import {
  submitScoringAsync,
  submitTaskAnswerScoringAsync
} from '../../services/players';
import { GameContext } from '../../contexts/game';
import {
  TaskList,
  TaskState
} from '../../components/facilitator/task/task-list';
import HelpSupport from '../../components/help-support';
import { ManualScoring } from '../../types/manual-scoring';
import { capitalizeFirstLetter } from '../../utils/string';
import { TaskContentAnswer } from '../../types/entities/task';
import { GridMode } from '../../components/grid/grid-tool-bar';
import {
  GetTaskById,
  GetTaskTitleByEntity
} from '../../utils/game-document/assets';
import { downloadFilesAndZip } from '../../utils/file-utility';
import { GameDocumentContext } from '../../contexts/game-document';
import { useGetAllPlayerStates } from '../../hooks/use-game-player-state';
import { TaskGrid } from '../../components/facilitator/task/task-grid';
import { GetResourceValue } from '../../utils/game-document/resources';
import { GetTaskContentByTaskId } from '../../utils/game-engine/player';
import {
  GetForm,
  GetTaskContentById
} from '../../utils/game-document/assets/task-contents';
import { AnswerType } from '../../types/game-document/entities/task-content';
import { FileZipDownloadRequest } from '../../types/facilitator/file-zip-download';
import { TaskContentGrid } from '../../components/facilitator/task/task-content-grid';
import { TaskDownloadWindow } from '../../features/facilitator/task/task-download-window';

export const AllTasks: React.FC<{ type: 'Task' | 'OnDemandTask' }> = ({
  type
}) => {
  const { gameCode } = useParams();
  const [game] = React.useContext(GameContext);
  const [gameDocument] = React.useContext(GameDocumentContext);
  const [gridMode, setGridMode] = React.useState<GridMode>('grid');
  const [data, setData] = React.useState<TaskState[]>([]);
  const [downloadOptionsVisible, setDownloadOptionsVisible] =
    React.useState<boolean>(false);

  const [manualScoringList, setTaskScorings] = useLocalStorage<ManualScoring[]>(
    `task-scorings-${gameCode}`,
    []
  );

  const taskList = (gameDocument.gameDocument?.assets.tasks ?? []).map(
    (t) => t.id
  );

  const { playerStates, refresh } = useGetAllPlayerStates(
    gameCode ?? '',
    game.gameState?.players ?? []
  );

  const submitScoring = async (scoring: ManualScoring) => {
    try {
      scoring.id = manualScoringList?.find(
        (x) =>
          x.formId === scoring.formId &&
          x.taskId === scoring.taskId &&
          x.playerCode === scoring.playerCode
      )?.id;
      if (!scoring.isScored) {
        //manual task answer scoring
        await submitScoringAsync(scoring);
      } else {
        //adjust task answer scoring
        await submitTaskAnswerScoringAsync(scoring);
      }
    } catch (error) {
      console.error('Failed submit scoring');
    } finally {
      setTaskScorings((prev) =>
        prev.filter(
          (t) =>
            !(
              t.formId === scoring.formId &&
              t.playerCode === scoring.playerCode &&
              t.taskId === scoring.taskId
            )
        )
      );
      refresh();
    }
  };

  const _exporter = React.createRef<ExcelExport>();

  const onExport = () => {
    toggleDownloadOptions();
  };

  const exportTasks = (type: 'photos' | 'videos' | 'all') => {
    switch (type) {
      case 'photos':
        photosExport();
        break;
      case 'videos':
        videosExport();
        break;
      default:
        excelExport();
        break;
    }
    toggleDownloadOptions();
  };

  const photosExport = () => exportByType('Image', 'photos');
  const videosExport = () => exportByType('Video', 'videos');

  const exportByType = (questionType: 'Image' | 'Video', suffix: string) => {
    const filesToDownload: FileZipDownloadRequest[] = data
      .filter((task) => task !== undefined)
      .filter(
        (task) =>
          task.questionType === questionType &&
          typeof task.answers === 'string' &&
          (task.answers as string).trim() !== ''
      )
      .flatMap((task) => {
        // There is a case that the answers are array of url separated by comma
        const urls = (task.answers as string).split(/,\s*/);

        // Map every URL to object file download
        return urls.map((url) => ({
          containerFolder: '',
          fileUrl: url
        }));
      });

    if (filesToDownload.length > 0) {
      downloadFilesAndZip(
        filesToDownload,
        `${gameDocument?.gameCode!}-All${suffix}.zip`
      );
    }
  };

  const excelExport = () => {
    if (_exporter.current) {
      _exporter.current.save();
    }
  };

  const generateTasks = () => {
    let tasks: TaskState[] = [];

    if (playerStates) {
      let data = playerStates.map((playerState) => {
        try {
          const filteredTasks = (playerState?.playerState!.tasks ?? []).filter(
            (t) =>
              type === 'Task'
                ? taskList.includes(t.id ?? '')
                : !taskList.includes(t.id ?? '')
          );
          return filteredTasks.map((task) => {
            const taskById = GetTaskById(
              gameDocument.gameDocument,
              task.id || ''
            );

            if (
              task.taskContentFormAnswers !== undefined &&
              task.taskContentFormAnswers.length > 0
            ) {
              return task?.taskContentFormAnswers?.map((taskContentAnswer) => {
                const taskContentById = GetTaskContentById(
                  gameDocument.gameDocument,
                  task.id || ''
                );
                let taskName = '';
                let question = '';
                let questionType = '';
                let scoringType = '';
                let taskContentId = '';
                let answers = '';

                if (
                  (taskById || taskContentById) &&
                  gameDocument.gameDocument
                ) {
                  const form = GetForm(
                    gameDocument.gameDocument,
                    (taskById ? taskById.taskContentId : taskContentById?.id) ||
                      '',
                    taskContentAnswer.formId || ''
                  );
                  if (form) {
                    taskName = GetTaskTitleByEntity(
                      gameDocument.gameDocument,
                      taskById,
                      taskContentById
                    );
                    question = GetResourceValue(
                      gameDocument.gameDocument,
                      form.questionResId || '',
                      ''
                    );
                    questionType = capitalizeFirstLetter(form.type || '');
                    scoringType = form.isManualScoring ? 'M' : 'A';
                    taskContentId =
                      (taskById
                        ? taskById.taskContentId
                        : taskContentById?.id) || '';
                    answers = buildAnswers(form.type, taskContentAnswer) || '';
                  }
                }

                return {
                  taskId: task?.id,
                  taskName,
                  question,
                  playerName: playerState?.playerState?.name,
                  playerCode: playerState?.playerState?.code,
                  questionType,
                  scoringType,
                  formId: taskContentAnswer.formId,
                  score: parseInt(taskContentAnswer?.score?.toString() ?? '0'),
                  taskContentId,
                  answers
                };
              });
            }

            if (task.dataARMiniGame) {
              const selectedTaskContent =
                type === 'Task'
                  ? GetTaskContentByTaskId(
                      gameDocument.gameDocument!,
                      task.id ?? ''
                    )
                  : GetTaskContentById(
                      gameDocument.gameDocument,
                      task.id ?? ''
                    );

              let taskName = '';
              let question = '';
              let questionType = '';
              let scoringType = '';
              let taskContentId = '';
              let answers = '';

              if (
                (taskById || selectedTaskContent) &&
                gameDocument.gameDocument
              ) {
                taskName = GetTaskTitleByEntity(
                  gameDocument.gameDocument,
                  taskById,
                  selectedTaskContent
                );
                scoringType = selectedTaskContent?.ARMiniGameSetting
                  ?.manualScoring
                  ? 'M'
                  : 'A';
                questionType = `${selectedTaskContent?.ARMiniGame} (AR)`;
                answers = task.dataARMiniGame;
                taskContentId =
                  (taskById
                    ? taskById.taskContentId
                    : selectedTaskContent?.id) || '';
              }

              return {
                taskId: task?.id,
                taskType: type,
                taskName,
                question,
                playerName: playerState?.playerState?.name,
                playerCode: playerState?.playerState?.code,
                questionType,
                scoringType,
                formId: task.id,
                score: task.dataARMiniGame.score ?? 0,
                taskContentId,
                answers
              };
            }
          });
        } catch (error) {}
      });

      if (data) {
        data.forEach((dataTasks) => {
          if (dataTasks) {
            dataTasks?.forEach((item) => {
              item !== undefined && tasks.push(item as TaskState);
            });
          }
        });
        setData(tasks.flat());
      }
    }
  };

  const buildAnswers = (
    answerType: AnswerType,
    taskContentAnswer: TaskContentAnswer
  ) => {
    if (answerType === 'checkbox' || answerType === 'radio') {
      return taskContentAnswer?.answers
        ?.map((x) => {
          {
            return GetResourceValue(
              gameDocument?.gameDocument!,
              x.answerResId,
              ''
            );
          }
        })
        ?.join(', ');
    } else {
      return taskContentAnswer?.answers?.map((x) => x.value).join(', ');
    }
  };

  const toggleDownloadOptions = () => {
    setDownloadOptionsVisible(!downloadOptionsVisible);
  };

  React.useEffect(() => {
    generateTasks();
  }, [playerStates]);

  return (
    <>
      <HelpSupport
        title="All tasks guide"
        url="https://forum.catalystglobal.com/t/4671"
      />
      {gridMode === 'grid' && (
        <TaskList
          type={type}
          playerStates={playerStates}
          onViewChange={(e) => setGridMode(e)}
          viewMode={gridMode}
          onSubmitScore={submitScoring}
          onExport={onExport}
        />
      )}

      {gridMode !== 'grid' && type === 'Task' && (
        <TaskGrid
          playerStates={playerStates}
          onViewChange={(e) => setGridMode(e)}
          viewMode={gridMode}
          onSubmitScore={submitScoring}
          onExport={onExport}
        />
      )}

      {gridMode !== 'grid' && type === 'OnDemandTask' && (
        <TaskContentGrid
          playerStates={playerStates}
          onViewChange={(e) => setGridMode(e)}
          viewMode={gridMode}
          onSubmitScore={submitScoring}
          onExport={excelExport}
        />
      )}

      <ExcelExport
        data={process(data, {})?.data}
        collapsible={true}
        fileName={`${gameDocument?.gameCode!}-AllTasks.xlsx`}
        ref={_exporter}>
        <ExcelExportColumn field="taskName" title="Task" width={200} />
        <ExcelExportColumn field="playerName" title="Player" width={200} />
        <ExcelExportColumn
          field="questionType"
          title="Question type"
          width={200}
        />
        <ExcelExportColumn field="scoringType" title="Type" width={100} />
        <ExcelExportColumn field="answers" title="Answer" width={500} />
        <ExcelExportColumn field="score" title="Score" width={200} />
      </ExcelExport>

      {downloadOptionsVisible && (
        <TaskDownloadWindow
          toggleDialog={toggleDownloadOptions}
          onDownload={exportTasks}
        />
      )}
    </>
  );
};
